import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface GalleryImage {
  src: string;
  alt: string;
}
const GALLERY_IMAGES: GalleryImage[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/bV7S2ieSET26iuCEoYa8RX/mary2.jpg',
  alt: 'A wrapped dancer performs before a field of projected light'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/bY6PxBcHfGpzMcs557mrFA/huxley-shadows2.jpg',
  alt: 'Six cinematic studies of figures moving through a dark architectural corridor'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/485zLkZcvDXYtdXaXVBjn7/chairs3.jpg',
  alt: 'An ensemble of dancers stretches across chairs beneath orange and violet light'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/a7JFruLabCEyQcjzeodd1z/yume-floor1.jpg',
  alt: 'Dancers move close to the floor in soft blue stage light'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/qhMJWicKAfRKRcCc3CeX9b/laser2.jpg',
  alt: 'A silhouetted dancer stands inside intersecting beams of blue and pink light'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fM3dsTAACHcEEaU2wSDhAR/huxley-shadows3.jpg',
  alt: 'Masked and unmasked dancers cross a warm, shadowed stage'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/hxEzHcWPhXZw2X8XbFxzW6/machines1.jpg',
  alt: 'A dancer advances through violet light before an assembled ensemble'
}];
export function GallerySection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const showPrevious = useCallback(() => {
    setActiveIndex(current => (current - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  }, []);
  const showNext = useCallback(() => {
    setActiveIndex(current => (current + 1) % GALLERY_IMAGES.length);
  }, []);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') showPrevious();
      if (event.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showNext, showPrevious]);
  const activeImage = GALLERY_IMAGES[activeIndex];
  return <section id="gallery" aria-label="Production photographs" className="relative h-screen min-h-[32rem] w-full overflow-hidden bg-ink" data-id="element-41" data-mp="3bde3xo5p3mny">
      <AnimatePresence mode="wait" initial={false} data-id="element-42" data-mp="32dr4v90fusy2">
        <motion.img key={activeImage.src} initial={{
        opacity: 0,
        scale: 1.015
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.65,
        ease: [0.16, 1, 0.3, 1]
      }} src={activeImage.src} alt={activeImage.alt} className="absolute inset-0 h-full w-full object-contain" data-id="element-43" data-mp="37ddgje0mc14r" />
      </AnimatePresence>

      <button type="button" onClick={showPrevious} className="group absolute inset-y-0 left-0 z-10 flex w-1/4 items-center justify-start px-4 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-sage sm:px-8" aria-label="Previous photograph" data-id="element-44" data-mp="vom97t9kb4ze">
        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-ivory/25 bg-ink/35 text-ivory/75 backdrop-blur-sm transition-all duration-500 group-hover:border-ivory/60 group-hover:bg-ink/60 group-hover:text-ivory" data-id="element-45" data-mp="2pwzrm3srv57v">
          <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-46" data-mp="2yyh7pm66j6wk" />
        </span>
      </button>

      <button type="button" onClick={showNext} className="group absolute inset-y-0 right-0 z-10 flex w-1/4 items-center justify-end px-4 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-sage sm:px-8" aria-label="Next photograph" data-id="element-47" data-mp="2uxdpwu4kn0sp">
        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-ivory/25 bg-ink/35 text-ivory/75 backdrop-blur-sm transition-all duration-500 group-hover:border-ivory/60 group-hover:bg-ink/60 group-hover:text-ivory" data-id="element-48" data-mp="19hok0hxd1no8">
          <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-49" data-mp="1fwnelgkb5qju" />
        </span>
      </button>
    </section>;
}